import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { ReactiveForm } from 'src/app/core/resources/reactive-form';
import { CargoResources } from '../manual-creation-cargo/resources/cargo';
import { Utils } from 'src/app/core/resources/utils';
import { CargoDetailService } from '../cargo-detail/cargo-detail.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Permission } from 'src/app/core/resources/permission';
import { Fmt } from 'src/app/core/messages/fmt';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { Origin } from 'src/app/core/interfaces/origin';
import { Destination } from 'src/app/core/interfaces/destination';
import { ConsignmentCityOption } from 'src/app/shared/consignment-card/consignment-card.component';
import { ThirdParty, ThirdPartyAddress } from 'src/app/core/interfaces/thirdParty';
import { LocationAddress } from 'src/app/core/interfaces/locationAddress';
import { ThirdPartyConsignmentService } from '../manual-creation-cargo/components/third-party-consignment/third-party-consignment.service';
import { Global } from 'src/app/core/resources/global';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { ConsignmentManager } from 'src/app/core/managers/consignment.manager';
import { StandardMapComponent } from 'src/app/shared/standard-map/standard-map.component';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { GeoValidationService } from 'src/app/core/services/geo-validation.service';
import {
  DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE,
  isDocumentRecreationInProgress
} from 'src/app/core/resources/transport-company-change.utils';

@Component({
  selector: 'app-consignment-form',
  templateUrl: './consignment-form.component.html',
  styleUrls: ['./consignment-form.component.scss'],
  providers: [CargoResources, ConsignmentManager]
})
export class ConsignmentFormComponent implements OnInit, OnDestroy {
  @ViewChild('standardMap') standardMap: StandardMapComponent;
  public consignmentId: string;
  public consignmentForm: FormGroup;
  public consignment: ConsignmentCargo;
  cargo: Cargo;
  senderCityOptions: { [key: string]: ConsignmentCityOption } = {};
  recipientCityOptions: { [key: string]: ConsignmentCityOption } = {};
  consignmentFieldsToCheck: { field: string, alias: string }[] = this.global.consignmentFieldsToCheck;
  formValidate = '';
  isCreation = false;
  mapOptions: OptionsAutocomplete = {};
  private polygonSubscriptions: Subscription[] = [];
  readonly documentRecreationInProgressMessage = DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE;

  constructor(
    private formBuilder: FormBuilder,
    private cargoManager: CargoManager,
    private utils: Utils,
    private global: Global,
    private cargoDetailService: CargoDetailService,
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public dialogParams: {
      previousConsignments?: ConsignmentCargo[],
      consignmentId?: string,
      consignment?: ConsignmentCargo,
      destinationId?: number,
      addressId?: number,
      cargo: Cargo
    },
    public dialogRef: MatDialogRef<ConsignmentFormComponent>,
    private thirdPartyConsignmentService: ThirdPartyConsignmentService,
    public consignmentManager: ConsignmentManager,
    private geoValidation: GeoValidationService
  ) {
  }

  ngOnInit() {
    this.checkData();
    this.setCityOptions();
    this.initForm();
    if (this.documentRecreationInProgress) this.consignmentForm.disable();
    this.setupPolygonSubscriptions();
  }

  ngOnDestroy() {
    this.polygonSubscriptions.forEach(s => s.unsubscribe());
    if (this.standardMap) this.standardMap.removeMunicipalityPolygon();
  }

  public onMapReady(): void {
    this.renderPolygonForRole('sender');
    this.renderPolygonForRole('recipient');
  }

  private setupPolygonSubscriptions(): void {
    if (!this.consignmentForm) return;
    (['sender', 'recipient'] as const).forEach(role => {
      const codeCtrl = this.consignmentForm.get(`${role}.municipalityCode`);
      if (!codeCtrl) return;
      this.polygonSubscriptions.push(
        codeCtrl.valueChanges.subscribe(() => this.renderPolygonForRole(role))
      );
    });
  }

  private renderPolygonForRole(role: 'sender' | 'recipient'): void {
    if (!this.standardMap) return;
    const codeCtrl = this.consignmentForm.get(`${role}.municipalityCode`);
    const cityId = codeCtrl && codeCtrl.value;
    this.standardMap.removeMunicipalityPolygon(role);
    if (!cityId) return;
    const color = role === 'sender' ? '#2563eb' : '#16a34a';
    this.geoValidation.getMunicipalityPolygon(cityId).subscribe(poly => {
      if (poly && poly.geometry && this.standardMap) {
        this.standardMap.renderMunicipalityPolygon(poly.geometry, { key: role, color, fillOpacity: 0.1 });
      }
    });
  }

  private checkData() {
    if (this.dialogParams && this.utils.isDefined(this.dialogParams.consignmentId) && this.utils.isDefined(this.dialogParams.cargo) &&
      this.utils.isDefined(this.dialogParams.consignment)) {
      this.consignmentId = this.dialogParams.consignmentId;
      this.cargo = this.dialogParams.cargo;
      this.consignment = this.dialogParams.consignment;
      this.isCreation = false;
    } else if (this.dialogParams && this.utils.isDefined(this.dialogParams.cargo)) {
      this.isCreation = true;
      this.cargo = this.dialogParams.cargo;
    } else {
      this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      this.dialogRef.close();
    }
  }

  private setCityOptions() {
    const { sender, destination } = this.cargoManager.getSenderAndDestinationConsignmentOptions(this.cargo);
    this.senderCityOptions = sender;
    this.recipientCityOptions = destination;
  }

  private initForm() {
    if (!this.isCreation) {
      this.consignmentForm = this.utils.createFormGroupFromObject(this.dialogParams.consignment);
      if (!this.consignmentForm.get('sender.fromDestination'))
        (this.consignmentForm.get('sender') as FormGroup).addControl('fromDestination', new FormControl(false));
      if (!this.consignmentForm.get('productDescription'))
        this.consignmentForm.addControl('productDescription', new FormControl(''));
      if (!this.consignmentForm.get('consolidationType'))
        this.consignmentForm.addControl('consolidationType', new FormGroup({
          id: new FormControl(''),
          name: new FormControl('')
        }))
      if (!this.consignmentForm.get('container'))
        this.consignmentForm.addControl('container', new FormControl('', this.consignmentManager.getContainerValidators(this.cargo)));
    } else {
      this.consignmentForm = this.consignmentManager.createConsignmentForm(this.cargo);
    }
  }

  private get documentId(): string {
    return this.consignmentId ? this.consignmentId.replace(/[^0-9]/g, '') : null;
  }

  public async onSubmit() {
    if (this.documentRecreationInProgress)
      return this.snackBarService.openSnackBar(
        this.documentRecreationInProgressMessage,
        undefined,
        'alert'
      );
    this.formValidate = 'touched';
    for (const field of this.consignmentFieldsToCheck) {
      if (!this.consignmentForm.get(field.field) || (this.consignmentForm.get(field.field).value !== 0 && !this.consignmentForm.get(field.field).value))
        return this.snackBarService.openSnackBar(Fmt.string(FormMessages.MISSING_FIELD, field.alias), undefined, 'alert');
    }
    const senderAddrCtrl = this.consignmentForm.get('sender.address');
    const recipientAddrCtrl = this.consignmentForm.get('recipient.address');
    if ((senderAddrCtrl && senderAddrCtrl.pending) || (recipientAddrCtrl && recipientAddrCtrl.pending))
      return this.snackBarService.openSnackBar('Validando dirección, espera un momento...', undefined, 'info');
    const senderOutside = senderAddrCtrl && senderAddrCtrl.errors && senderAddrCtrl.errors.outsideMunicipality;
    const recipientOutside = recipientAddrCtrl && recipientAddrCtrl.errors && recipientAddrCtrl.errors.outsideMunicipality;
    if (senderOutside || recipientOutside)
      return this.snackBarService.openSnackBar('Una o más direcciones están fuera del municipio seleccionado', undefined, 'alert');
    const isValidByVariableFields = this.consignmentManager.getCompleteVariableFields(this.consignmentForm.getRawValue(), this.cargo);
    if (!isValidByVariableFields.valid)
      return this.snackBarService.openSnackBar(isValidByVariableFields.error, undefined, 'alert');
    const isValidByDangerousFields = this.consignmentManager.getCompleteDangerousFields(this.consignmentForm.getRawValue());
    if (!isValidByDangerousFields.valid)
      return this.snackBarService.openSnackBar(isValidByDangerousFields.error, undefined, 'alert');
    const consignmentToCheck: ConsignmentCargo = this.consignmentForm.getRawValue();
    const consignment = this.checkConsignment(consignmentToCheck);
    if (this.isCreation) {
      const { duplicated, duplicatedConsignments } = this.cargoManager.verifyDuplicatedConsignments(
        [...this.dialogParams.previousConsignments, this.utils.removeNullUndefinedEmpty(consignment)]
      );
      if (duplicated)
        return this.snackBarService.openSnackBar(`Las remesas ${duplicatedConsignments.map(c => c.id ? c.id : 'la nueva remesa').join(' y ')} son iguales`, undefined, 'alert');
    }
    if (this.isCreation) this.createConsignment(consignment);
    else this.snackBarService.openSnackBar("No se puede editar una remesa creada", undefined, "error");
  }

  private async createConsignment(consignment: ConsignmentCargo) {
    if (this.documentRecreationInProgress) {
      this.snackBarService.openSnackBar(
        this.documentRecreationInProgressMessage,
        undefined,
        'alert'
      );
      return;
    }
    this.spinner.show();
    let success = null;
    try {
      success = await this.cargoDetailService.createAndEmitConsignment({ cargoId: this.cargo.id, emitToRndc: true, updateCargoState: true, consignment: consignment }).toPromise();
      this.spinner.hide();
      this.snackBarService.openSnackBar("Remesa creada correctamente");
      this.dialogRef.close({ state: true });
    } catch (error) {
      this.spinner.hide();
      this.snackBarService.openSnackBar("Ocurrió un error al crear la remesa", undefined, "error");
    }
  }

  private checkConsignment(consignment: ConsignmentCargo): ConsignmentCargo {
    const consignmentToCheck: ConsignmentCargo = this.utils.clone(consignment);
    consignmentToCheck.errorCreation = null;
    consignmentToCheck.errorFinalFulfillment = null;
    consignmentToCheck.errorCancellation = null;
    consignmentToCheck.errorInitialFulfillment = null;
    if (!consignmentToCheck.requiresSubSection) delete consignmentToCheck.subSection;
    if (!consignmentToCheck.requiresTaxCode) delete consignmentToCheck.tax;
    if (!consignmentToCheck.isDangerous) delete consignmentToCheck.dangerousMerchandise;
    if (!consignmentToCheck.isHazardousWaste) delete consignmentToCheck.hazardousWaste;
    if (!this.consignmentManager.consignmentRequiresDescription(consignmentToCheck)) delete consignmentToCheck.productDescription;
    if (!this.consignmentManager.consignmentRequiresConsolidationType(consignmentToCheck)) delete consignmentToCheck.consolidationType;
    if (!this.consignmentManager.consignmentRequiresAmountProduct(consignmentToCheck)) {
      delete consignmentToCheck.amountProduct;
      delete consignmentToCheck.unitMeasureProduct;
    }
    const isContainerService = this.utils.getNestedValue(this.cargo, 'cargoModel.cargoType.name') === 'Contenedor';
    if (!isContainerService) delete consignmentToCheck.container;
    return consignmentToCheck;
  }

  get documentRecreationInProgress(): boolean {
    return isDocumentRecreationInProgress(this.cargo);
  }
}
